import { useMutation, useQuery } from "@apollo/client";
import { HmbpSection } from "encamp-shared/src/hmbp";
import { gql } from "generated-graphql";
import {
  DynamicField,
  GetReportDetailsQuery,
  Issue,
  Permission,
  ProgramArea,
  TierIiReportOrgStatus,
  TierIiReportStep,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useMeasurements } from "hooks/useMeasurements";
import { useProducts } from "hooks/useProducts";
import { useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { IssueSegment } from "util/issues";
import {
  ReportStepMetadata,
  reportStepMetadata,
  ReportStepState,
} from "util/reportStepMetadata";
import { ReportContext } from "./ReportProvider";
import { isHmbpIssue, useHmbpFeature, useHmbpIssues } from "./useHmbp";

export type Report = GetReportDetailsQuery["tierIIReport"];

gql(`
  fragment person on Person {
    id
    tenantId
    first
    last
    email
    isEncamp
    phones {
      number
      type
    }
    attentionTo
    streetAddress1
    streetAddress2
    city
    state
    zip
    dbNumber
  }
`);

gql(`
  fragment facilityContact on FacilityContact {
    id
    isPrimaryContact
    reportingRoles
    person {
        ...person
    }
}`);

gql(`
  fragment facilityContacts on Facility {
    contacts(programArea: $programArea) {
      ...facilityContact
    }
  }
`);

gql(`
  fragment reportFacilitySection on Facility {
        id
        name
        dateClosed
        dateInactive
        colocationReportingFacilityId
        isClosed
        isInactive
        isNotReporting
        isNotReportingThroughEncamp
        ...collectionMode
        ...regulatoryInfo
        ...facilityInfo
        ...stateInfo
        ...facilityContacts
  }
`);

export const GetReportDetailsQueryName = "GetReportDetails";
export const GET_REPORT = gql(`
  query GetReportDetails($reportId: ID!, $programArea: ProgramArea, $version: String) {
    tierIIReport(reportId: $reportId, version: $version) {
      id
      tenantId
      reportingYear
      reportKind
      organizationStatus
      encampStatus
      verifiedAt
      filedBy
      filedOn
      verifierFullName
      verifierPhone
      verifierEmail
      verifierTitle
      notesToRegulators
      reasonForReport
      requiresReviewers
      touchedSteps
      touchedCASteps
      department
      reviewCompletedByUserId
      facility {
        ...reportFacilitySection
      }
      assignees {
        id
        assignmentType
        isComplete
        completedAt
        updatedAt
        person {
          ...person
        }
      }
      reviewers {
        id
        assignmentType
        isComplete
        completedAt
        updatedAt
        person {
          ...person
        }
      }
      documents {
        ...DocumentGridRow
        facilities {
          id
          name
        }
        activities {
          id
          description
          lobMail {
            status
          }
        }
      }
      issues {
        jurisdictions
        key
        message
        modelName
        dataName
        schemaStack {
          model
          id
          name
        }
        modelId
        metadata
        facilityId
        tenantId
        level
      }
      undergroundStorageTankStatuses {
        undergroundStorageTankId
        touchedSteps
      }
      currentWorkflow {
        id
        currentActivityId
        activities {
          id
          type
          displayName
          description
          status
          updatedAt
          documents {
            document {
              id
              title
              description
              storageLink
              documentType
            }
          }
        }
      }
    }
  }`);

const UPDATE_FACILITY_REPORT = gql(`
  mutation UpdateFacilityReport($id: ID!, $facility: FacilityInput!, $reportId: ID, $programArea: ProgramArea) {
    updateFacility(id: $id, facility: $facility, reportId: $reportId) {
      ...reportFacilitySection
    }
  }`);

export function useUpdateFacilityReport() {
  return useMutation(UPDATE_FACILITY_REPORT, {
    refetchQueries: [GET_REPORT],
  });
}

export function useReport(options: any = {}) {
  // Try to get the value from context
  const contextValue = useContext(ReportContext);
  // Always get reportId from params
  const { reportId = "" } = useParams<{ reportId: string }>();
  // Only run the query if context is not present and reportId exists
  const shouldQuery = !contextValue && !!reportId;
  const queryResult = useQuery(GET_REPORT, {
    variables: {
      reportId,
      programArea: ProgramArea.Epcra,
      version: undefined,
    },
    skip: !shouldQuery,
    ...options,
  });
  // Return context if present, otherwise query result
  return contextValue || queryResult;
}

export const regulatoryFieldsExceptFacilityAlternateIds = [
  "isSubjectToChemicalAccidentPrevention",
  "isSubjectToEmergencyPlanning",
  "isSubjectToToxicsReleaseInventoryReporting",
];

// For report validation, these fields that are Regulatory Info fields and
// are not validated on the Facility Profile page
export const regulatoryFields = [
  ...regulatoryFieldsExceptFacilityAlternateIds,
  "facilityAlternateIds",
];

// returns the list of all facility state fields from the report
export function useReportDynamicFields(
  filterFn?: (field: DynamicField) => boolean
): DynamicField[] {
  const { data } = useReport();
  return useMemo(
    () => filterFields(data?.tierIIReport.facility.stateFields ?? [], filterFn),
    [data?.tierIIReport.facility.stateFields, filterFn]
  );
}

export function useReportIssues(
  filterFn?: (issue: Issue, segments: IssueSegment[]) => boolean
): Issue[] {
  const { data } = useReport();

  return useMemo(
    () =>
      filterIssues(
        data?.tierIIReport.issues?.filter(
          (i) => i.metadata?.ignoreForReport !== true
        ) ?? [],
        (issue) => {
          return filterFn?.(issue, issue.schemaStack ?? []) ?? true;
        }
      ),
    [data?.tierIIReport.issues, filterFn]
  );
}

export function useIgnoredIssues() {
  const { data } = useReport();

  return useMemo(
    () =>
      filterIssues(
        data?.tierIIReport.issues?.filter(
          (i) => i.metadata?.ignoreForReport === true
        ) ?? []
      ),
    [data?.tierIIReport.issues]
  );
}

export function useFacilityInfoIssues() {
  return useReportIssues(
    (issue) =>
      issue.modelName === "Facility" &&
      issue.key !== "contacts" &&
      issue.key !== "documents" &&
      issue.key !== "facilityDocuments" &&
      issue.key !== "reportFacilityDocuments" &&
      issue.key !== "facilityChemicals" &&
      issue.key !== "undergroundStorageTanks" &&
      // these are the errors related to the actual whole of the list of
      // reporting facility chemicals, currently it's just a validation that
      // there is at least 1 RFC, so we dont want that on the facility tab
      issue.key !== "reportingFacilityChemicals" &&
      !regulatoryFields.some((re) => issue.key?.startsWith(re))
  );
}

export function useRegulatoryInfoIssues() {
  const { data } = useReport();
  const isHmbp = useHmbpFeature(data?.tierIIReport?.facility.state ?? "");
  return useReportIssues((i) => filterRegulatoryInfoIssues(i, isHmbp));
}

export function filterRegulatoryInfoIssues(
  issue: Issue,
  isHmbp: boolean | undefined
): boolean {
  return (
    (issue.modelName === "Facility" &&
      regulatoryFields.some((re) => issue.key?.startsWith(re))) ||
    (isHmbp === true && isHmbpIssue(issue, { section: HmbpSection.Regulatory }))
  );
}

export function useReviewIssues() {
  return useReportIssues((issue) => issue.key === "reasonForReport");
}

export function useProductIssues() {
  return useReportIssues((issue, segments) => {
    return segments.some(
      (segment) =>
        segment.model === "FacilityProduct" ||
        segment.model === "FacilityProductMeasurement"
    );
  });
}

export function useChemicalIssues() {
  return useReportIssues((issue, segments) => {
    // use the segments or you'll get a faulty match for ReportingFacilityChemical
    return (
      segments.some((segment) => segment.model === "FacilityChemical") ||
      segments.some(
        (segment) => segment.model === "FacilityChemicalMeasurement"
      )
    );
  });
}

export function useReportingFacilityChemicalIssues() {
  const issues = useReportIssues(
    (issue, segments) =>
      segments.some(
        (segment) => segment.model === "ReportingFacilityChemical"
      ) &&
      segments.every(
        (segment) => segment.model !== "ReportingFacilityChemicalOrigin"
      )
  );
  return issues;
}

export function useContactIssues() {
  return useFacilityContactIssues().concat(useFacilityContactModelIssues());
}

export function useFacilityContactIssues() {
  return useReportIssues((issue) => issue.key === "contacts");
}

export function useReportingFacilityChemicalsIssues() {
  return useReportIssues((issue) => issue.key === "reportingFacilityChemicals");
}

export function useFacilityContactModelIssues() {
  return useReportIssues(
    (issue) =>
      issue.modelName === "FacilityContact" || issue.modelName === "Person"
  );
}

export function useFacilityContactIssuesForModel(modelId: string) {
  return useReportIssues(
    (issue) =>
      issue.modelName === "FacilityContact" && issue.modelId === modelId
  );
}

export function usePersonIssuesForModel(modelId: string) {
  return useReportIssues(
    (issue) => issue.modelName === "Person" && issue.modelId === modelId
  );
}

export function useDocumentIssuesForModel(modelId: string) {
  return useAllDocumentIssues().filter((issue) => issue.modelId === modelId);
}

export function useReportFacilityDocumentIssues() {
  return useReportIssues(
    (issue) =>
      issue.key === "reportFacilityDocuments" &&
      issue.modelName === "TierIIReport"
  );
}

export function useReportFacilityDocumentModelIssues() {
  return useReportIssues(
    (issue) => issue.modelName === "ReportFacilityDocument"
  );
}

export function useDocumentIssues() {
  return useReportIssues((issue) => issue.modelName === "Document");
}

export function useAllDocumentIssues() {
  const documentIssues = useDocumentIssues();
  const reportFacilityDocumentIssues = useReportFacilityDocumentIssues();
  const reportFacilityDocumentModelIssues =
    useReportFacilityDocumentModelIssues();
  const allIssues = reportFacilityDocumentIssues
    .concat(reportFacilityDocumentModelIssues)
    .concat(documentIssues);
  const dedupedByDocumentIdAndMessage = allIssues.filter(
    (issue, index, self) =>
      index ===
      self.findIndex(
        (t) => t.modelId === issue.modelId && t.message === issue.message
      )
  );
  return dedupedByDocumentIdAndMessage;
}

export function useStateInfoIssues() {
  const { data } = useReport();
  const hmbp = useHmbpFeature(data?.tierIIReport.facility.state ?? "");
  const hmbpIssues = useHmbpIssues();
  const stateFieldIssues = useReportIssues(
    (issue) => issue.modelName === "FacilityStateField"
  );
  return hmbp ? hmbpIssues : stateFieldIssues;
}

export function useVerifyAndSubmitIssues() {
  return useReportIssues(
    (issue) =>
      issue.modelName === "TierIIReport" ||
      issue.metadata?.reportSection === "VerifyAndSubmit"
  );
}

export function useIssuesGroupedByStep() {
  return {
    [TierIiReportStep.Facility]: useFacilityInfoIssues(),
    [TierIiReportStep.Chemicals]: useChemicalIssues(), // TODO: will this be Measurement, FacilityChemical, RFC, or all three?
    [TierIiReportStep.Products]: useProductIssues(), // TODO: this will be FacilityProduct issues
    [TierIiReportStep.OtherChemicals]: useChemicalIssues(), // TODO: this will be FacilityChemical issues
    [TierIiReportStep.ChemicalSummary]: useReportingFacilityChemicalIssues(),
    [TierIiReportStep.Contacts]: useContactIssues(),
    [TierIiReportStep.Documents]: useAllDocumentIssues(),
    [TierIiReportStep.Regulatory]: useRegulatoryInfoIssues(),
    [TierIiReportStep.ReportOverview]: [],
    [TierIiReportStep.Review]: useReviewIssues(),
    [TierIiReportStep.StateInformation]: useStateInfoIssues(),
    [TierIiReportStep.VerifyAndSubmit]: useVerifyAndSubmitIssues(),
  };
}

export function filterIssues(
  issues: Issue[],
  filterFn?: (issue: Issue) => boolean
): Issue[] {
  return issues.filter((issue) => filterFn?.(issue) ?? true) ?? [];
}

export function filterFields(
  fields: DynamicField[],
  filterFn?: (field: DynamicField) => boolean
): DynamicField[] {
  return fields.filter((field) => filterFn?.(field) ?? true) ?? [];
}

export type StepState = {
  status: ReportStepState;
  issues: Issue[];
  metaData: ReportStepMetadata;
};

export type ReportSteps = {
  [key in TierIiReportStep]?: StepState;
};

export function useReportSteps(): ReportSteps {
  const { data } = useReport();
  const report = data?.tierIIReport;
  const issues = useIssuesGroupedByStep();
  const chemicalSteps = useChemicalSteps(data?.tierIIReport.facility.id, true);
  const { hasPermissionForFacility } = useAuthorization();
  const canVerifyAndSubmit = useMemo(
    () =>
      hasPermissionForFacility(report?.facility.id ?? "", [
        Permission.VerifyTierIiReport,
      ]),
    [report?.facility.id, hasPermissionForFacility]
  );
  // this makes it easier to place the state step in the correct order
  // instead of splicing it in by index
  const stateStep = useMemo(
    () =>
      report?.facility.stateFields.length
        ? [TierIiReportStep.StateInformation]
        : [],
    [report]
  );

  const reportSteps: ReportSteps = useMemo(() => {
    if (
      !report ||
      report.organizationStatus === TierIiReportOrgStatus.NotReporting
    ) {
      return {};
    }

    let steps: TierIiReportStep[] = [];
    if (report.organizationStatus === TierIiReportOrgStatus.Verified) {
      steps = [
        TierIiReportStep.ReportOverview,
        TierIiReportStep.Facility,
        TierIiReportStep.Regulatory,
        TierIiReportStep.ChemicalSummary,
        TierIiReportStep.Contacts,
        TierIiReportStep.Documents,
        ...stateStep,
        TierIiReportStep.Review,
      ];
    } else {
      steps = [
        TierIiReportStep.ReportOverview,
        TierIiReportStep.Facility,
        TierIiReportStep.Regulatory,
        ...chemicalSteps,
        TierIiReportStep.Contacts,
        TierIiReportStep.Documents,
        ...stateStep,
        TierIiReportStep.Review,
      ];

      if (canVerifyAndSubmit) {
        steps.push(TierIiReportStep.VerifyAndSubmit);
      }
    }

    const stepsObject: ReportSteps = {};

    steps.forEach((step) => {
      let stepIssues: Issue[] = [];
      if (step !== TierIiReportStep.VerifyAndSubmit) {
        stepIssues = issues[step] ?? [];
      }
      stepsObject[step] = {
        status: getStepStatus(step, report, issues),
        issues: stepIssues,
        metaData: reportStepMetadata[step],
      };
    });
    return stepsObject;
  }, [report, chemicalSteps, stateStep, canVerifyAndSubmit, issues]);

  return reportSteps;
}

export function useStepsByStatus(status: ReportStepState) {
  const stepStatuses = useReportSteps();
  return Object.entries(stepStatuses)
    .filter(([, stat]) => stat.status === status)
    .map(([step]) => step as TierIiReportStep);
}

export function useInvalidSteps() {
  return useStepsByStatus(ReportStepState.Invalid);
}

export function useNotStartedSteps() {
  return useStepsByStatus(ReportStepState.NotStarted);
}

export function useDoneSteps() {
  return useStepsByStatus(ReportStepState.Done);
}

export function useTouchedSteps() {
  const report = useReport();
  return report.data?.tierIIReport.touchedSteps ?? [];
}

export function useStepStatus(step: TierIiReportStep): ReportStepState {
  const { data } = useReport();
  const issues = useIssuesGroupedByStep();
  if (!data) return ReportStepState.NotStarted;

  return getStepStatus(step, data.tierIIReport, issues);
}

function getStepStatus(
  step: TierIiReportStep,
  report: Report,
  issues: { [key in TierIiReportStep]?: Issue[] }
): ReportStepState {
  const touchedSteps = report.touchedSteps;
  const hasCompletedReview = report.reviewCompletedByUserId;
  const hasReviewBeenTouched = touchedSteps?.includes(TierIiReportStep.Review);
  const hasIssues = issues[step]?.length && issues[step].length > 0;
  const isStepTouched = touchedSteps?.includes(step);
  const isVerified =
    report.organizationStatus === TierIiReportOrgStatus.Verified;
  const hasNoStateFields = report.facility.stateFields.length === 0;

  // Handle Review step
  if (step === TierIiReportStep.Review) {
    if (hasCompletedReview) {
      return ReportStepState.Done;
    }

    if (hasIssues && hasReviewBeenTouched) {
      return ReportStepState.Invalid;
    }

    return ReportStepState.NotStarted;
  }

  // Handle State Information step
  if (step === TierIiReportStep.StateInformation) {
    if (hasNoStateFields) {
      return ReportStepState.Done;
    }
  }

  // Handle Verify & Submit step
  if (step === TierIiReportStep.VerifyAndSubmit) {
    return isVerified ? ReportStepState.Done : ReportStepState.NotStarted;
  }

  // Handle all other steps
  if (hasIssues) {
    return ReportStepState.Invalid;
  }

  return isStepTouched ? ReportStepState.Done : ReportStepState.NotStarted;
}

export function stepToRoute(step: TierIiReportStep) {
  return reportStepMetadata[step].route;
}

export const stepToDisplayName: Record<TierIiReportStep, string> = {
  [TierIiReportStep.ReportOverview]: "Overview",
  [TierIiReportStep.Facility]: "Facility",
  [TierIiReportStep.Regulatory]: "Regulatory",
  [TierIiReportStep.Chemicals]: "Chemicals",
  [TierIiReportStep.Products]: "Products",
  [TierIiReportStep.OtherChemicals]: "Other Chemicals",
  [TierIiReportStep.ChemicalSummary]: "Chemical Summary",
  [TierIiReportStep.Contacts]: "Contacts",
  [TierIiReportStep.Documents]: "Documents",
  [TierIiReportStep.StateInformation]: "State Information",
  [TierIiReportStep.Review]: "Review",
  [TierIiReportStep.VerifyAndSubmit]: "Verify & Submit",
};

export function useChemicalSteps(
  facilityId: string | undefined,
  showChemicalSummary?: boolean
) {
  const { hasProducts } = useProducts();
  const { hasMeasurements } = useMeasurements(facilityId);

  if (hasProducts) {
    return [
      TierIiReportStep.Products,
      TierIiReportStep.OtherChemicals,
      TierIiReportStep.ChemicalSummary,
    ];
  }

  if (hasMeasurements) {
    return [TierIiReportStep.Chemicals, TierIiReportStep.ChemicalSummary];
  }

  if (showChemicalSummary) {
    return [TierIiReportStep.Chemicals, TierIiReportStep.ChemicalSummary];
  }
  return [TierIiReportStep.Chemicals];
}
